import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface SubLevelConfig {
  numberOfSubLevels: number;
  useFixedMinutes?: boolean;
  fixedMinutes?: number;
  displayMinutesHand?: boolean;
  useFixedSeconds?: boolean;
  fixedSeconds?: number;
  displaySecondsHand?: boolean;
  hoursVisible?: boolean;
  hoursMarksVisible?: boolean;
  zonesVisible?: boolean;
  minutesVisible?: boolean;
  noHint?: boolean;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevelConfig[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;

const OBJECTIVE_PT: Record<string, string> = {
  'Full hours.': 'Horas cheias (sem minutos).',
  'Half hours.': 'Meia hora (X:30).',
  'Quarters of an hour.': 'Quartos de hora (X:15 e X:45).',
  'Time containing minutes.': 'Hora com minutos quaisquer.',
  'Time containing minutes and seconds.': 'Hora com minutos e segundos.',
  'No hints.': 'Sem dicas — só os ponteiros!',
};

const CLOCK_R = 160;
const CENTER = 200;
const VIEWBOX = 400;

function fmtTime(h: number, m: number, s: number, showSec: boolean, use24 = false, tarde = false): string {
  // Modo adulto (F2): horário como no ponto, no ônibus e no remédio — 24h.
  // A mecânica continua no mostrador de 12h: ler "15:25" e marcar 3:25 É a habilidade.
  const hh = use24 ? String(h + (tarde ? 12 : 0)).padStart(2, '0') : String(h === 0 ? 12 : h);
  const mm = m.toString().padStart(2, '0');
  if (!showSec) return `${hh}:${mm}`;
  const ss = s.toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function angleHour(h: number, m: number, s: number): number {
  return (h % 12) * 30 + m * 0.5 + s / 120 - 90;
}
function angleMinute(m: number, s: number): number {
  return m * 6 + s * 0.1 - 90;
}
function angleSecond(s: number): number {
  return s * 6 - 90;
}

function polar(cx: number, cy: number, r: number, angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

export function mountClockgame(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const objectivePt = OBJECTIVE_PT[level.objective] ?? level.objective;
  const totalSubs = level.data.reduce((acc, d) => acc + d.numberOfSubLevels, 0);

  let dataIdx = 0;
  let subWithinData = 0;
  let correctCount = 0;
  const use24 = ctx.publico === 'adulto';
  let targetTarde = false;
  let targetH = 0;
  let targetM = 0;
  let targetS = 0;
  let currentH = 0;
  let currentM = 0;
  let currentS = 0;
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Aprender o relógio</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage clock">
      <div class="feedback" aria-live="polite"></div>
      <p class="clock__progress" aria-live="polite"></p>
      <p class="clock__hint">${objectivePt}</p>
      <div class="clock__target" aria-live="polite"></div>
      <div class="clock__face-wrap"></div>
      <div class="clock__controls"></div>
      <div class="clock__actions">
        <button class="btn btn--primary clock__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.clock__progress')!;
  const targetEl = page.querySelector<HTMLElement>('.clock__target')!;
  const faceWrap = page.querySelector<HTMLElement>('.clock__face-wrap')!;
  const controlsEl = page.querySelector<HTMLElement>('.clock__controls')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.clock__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  confirmBtn.addEventListener('click', validate);

  function currentConfig(): SubLevelConfig {
    return level.data[dataIdx]!;
  }

  function currentOverall(): number {
    let acc = 0;
    for (let i = 0; i < dataIdx; i++) acc += level.data[i]!.numberOfSubLevels;
    return acc + subWithinData;
  }

  function startQuestion(): void {
    const cfg = currentConfig();
    targetH = Math.floor(Math.random() * 12);
    targetTarde = use24 && Math.random() < 0.5; // exibe 03:25 ou 15:25; ponteiros iguais
    if (cfg.useFixedMinutes) targetM = cfg.fixedMinutes ?? 0;
    else if (cfg.displayMinutesHand) targetM = Math.floor(Math.random() * 60);
    else targetM = 0;
    if (cfg.useFixedSeconds) targetS = cfg.fixedSeconds ?? 0;
    else if (cfg.displaySecondsHand) targetS = Math.floor(Math.random() * 60);
    else targetS = 0;

    do { currentH = Math.floor(Math.random() * 12); } while (currentH === targetH);
    if (cfg.displayMinutesHand && !cfg.useFixedMinutes) {
      do { currentM = Math.floor(Math.random() * 60); } while (currentM === targetM);
    } else currentM = cfg.useFixedMinutes ? targetM : 0;
    if (cfg.displaySecondsHand && !cfg.useFixedSeconds) {
      do { currentS = Math.floor(Math.random() * 60); } while (currentS === targetS);
    } else currentS = cfg.useFixedSeconds ? targetS : 0;

    buttonsBlocked = false;
    renderAll();
  }

  function renderAll(): void {
    renderProgress();
    renderTarget();
    renderFace();
    renderControls();
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${currentOverall() + 1} de ${totalSubs}`;
  }

  function renderTarget(): void {
    const cfg = currentConfig();
    const showSec = !!cfg.displaySecondsHand;
    targetEl.innerHTML = `Marque o horário: <strong>${fmtTime(targetH, targetM, targetS, showSec, use24, targetTarde)}</strong>`;
  }

  function renderFace(): void {
    const cfg = currentConfig();
    const showZones = cfg.zonesVisible ?? true;
    const showHoursMarks = cfg.hoursMarksVisible ?? true;
    const showHours = cfg.hoursVisible ?? true;
    const showMinutes = cfg.minutesVisible ?? true;
    const showMinutesHand = !!cfg.displayMinutesHand;
    const showSecondsHand = !!cfg.displaySecondsHand;

    faceWrap.innerHTML = '';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${VIEWBOX} ${VIEWBOX}`);
    svg.setAttribute('class', 'clock__face');

    if (showZones) {
      const quadrants = [
        { startAngle: -90, endAngle: 0, cls: 'clock__zone--q1' },
        { startAngle: 0, endAngle: 90, cls: 'clock__zone--q2' },
        { startAngle: 90, endAngle: 180, cls: 'clock__zone--q3' },
        { startAngle: 180, endAngle: 270, cls: 'clock__zone--q4' },
      ];
      for (const q of quadrants) {
        const [x1, y1] = polar(CENTER, CENTER, CLOCK_R, q.startAngle);
        const [x2, y2] = polar(CENTER, CENTER, CLOCK_R, q.endAngle);
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', `M ${CENTER} ${CENTER} L ${x1} ${y1} A ${CLOCK_R} ${CLOCK_R} 0 0 1 ${x2} ${y2} Z`);
        path.setAttribute('class', `clock__zone ${q.cls}`);
        svg.appendChild(path);
      }
    }

    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    bg.setAttribute('cx', String(CENTER));
    bg.setAttribute('cy', String(CENTER));
    bg.setAttribute('r', String(CLOCK_R));
    bg.setAttribute('class', 'clock__bg-circle');
    if (showZones) {
      bg.style.fill = 'none';
      bg.style.pointerEvents = 'none';
    }
    svg.appendChild(bg);

    if (showHoursMarks) {
      for (let i = 0; i < 12; i++) {
        const angle = i * 30 - 90;
        const [x1, y1] = polar(CENTER, CENTER, CLOCK_R - 14, angle);
        const [x2, y2] = polar(CENTER, CENTER, CLOCK_R - 2, angle);
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(x1));
        line.setAttribute('y1', String(y1));
        line.setAttribute('x2', String(x2));
        line.setAttribute('y2', String(y2));
        line.setAttribute('class', 'clock__mark-hour');
        svg.appendChild(line);
      }
      for (let i = 0; i < 60; i++) {
        if (i % 5 === 0) continue;
        const angle = i * 6 - 90;
        const [x1, y1] = polar(CENTER, CENTER, CLOCK_R - 6, angle);
        const [x2, y2] = polar(CENTER, CENTER, CLOCK_R - 2, angle);
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(x1));
        line.setAttribute('y1', String(y1));
        line.setAttribute('x2', String(x2));
        line.setAttribute('y2', String(y2));
        line.setAttribute('class', 'clock__mark-min');
        svg.appendChild(line);
      }
    }

    if (showHours) {
      for (let i = 1; i <= 12; i++) {
        const angle = i * 30 - 90;
        const [x, y] = polar(CENTER, CENTER, CLOCK_R - 30, angle);
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', String(x));
        text.setAttribute('y', String(y));
        text.setAttribute('class', 'clock__number-hour');
        text.textContent = String(i);
        svg.appendChild(text);
      }
    }

    if (showMinutes && showHoursMarks) {
      for (let i = 1; i <= 12; i++) {
        const angle = i * 30 - 90;
        const [x, y] = polar(CENTER, CENTER, CLOCK_R + 14, angle);
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', String(x));
        text.setAttribute('y', String(y));
        text.setAttribute('class', 'clock__number-min');
        text.textContent = String((i * 5) % 60);
        svg.appendChild(text);
      }
    }

    const handHour = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    const [hx, hy] = polar(CENTER, CENTER, CLOCK_R * 0.55, angleHour(currentH, currentM, currentS));
    handHour.setAttribute('x1', String(CENTER));
    handHour.setAttribute('y1', String(CENTER));
    handHour.setAttribute('x2', String(hx));
    handHour.setAttribute('y2', String(hy));
    handHour.setAttribute('class', 'clock__hand clock__hand--hour');
    svg.appendChild(handHour);

    if (showMinutesHand) {
      const handMin = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const [mx, my] = polar(CENTER, CENTER, CLOCK_R * 0.85, angleMinute(currentM, currentS));
      handMin.setAttribute('x1', String(CENTER));
      handMin.setAttribute('y1', String(CENTER));
      handMin.setAttribute('x2', String(mx));
      handMin.setAttribute('y2', String(my));
      handMin.setAttribute('class', 'clock__hand clock__hand--min');
      svg.appendChild(handMin);
    }

    if (showSecondsHand) {
      const handSec = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const [sx, sy] = polar(CENTER, CENTER, CLOCK_R * 0.9, angleSecond(currentS));
      handSec.setAttribute('x1', String(CENTER));
      handSec.setAttribute('y1', String(CENTER));
      handSec.setAttribute('x2', String(sx));
      handSec.setAttribute('y2', String(sy));
      handSec.setAttribute('class', 'clock__hand clock__hand--sec');
      svg.appendChild(handSec);
    }

    const center = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    center.setAttribute('cx', String(CENTER));
    center.setAttribute('cy', String(CENTER));
    center.setAttribute('r', '7');
    center.setAttribute('class', 'clock__center');
    svg.appendChild(center);

    faceWrap.appendChild(svg);
  }

  function renderControls(): void {
    const cfg = currentConfig();
    const showMin = !!cfg.displayMinutesHand;
    const showSec = !!cfg.displaySecondsHand;
    const fixedM = !!cfg.useFixedMinutes;
    const fixedS = !!cfg.useFixedSeconds;
    controlsEl.innerHTML = '';

    controlsEl.appendChild(makeRow('Hora', currentH === 0 ? 12 : currentH, [
      { label: '−1', delta: () => { currentH = (currentH + 11) % 12; renderAll(); } },
      { label: '+1', delta: () => { currentH = (currentH + 1) % 12; renderAll(); } },
    ]));

    if (showMin && !fixedM) {
      controlsEl.appendChild(makeRow('Minuto', currentM, [
        { label: '−5', delta: () => { currentM = (currentM + 55) % 60; renderAll(); } },
        { label: '−1', delta: () => { currentM = (currentM + 59) % 60; renderAll(); } },
        { label: '+1', delta: () => { currentM = (currentM + 1) % 60; renderAll(); } },
        { label: '+5', delta: () => { currentM = (currentM + 5) % 60; renderAll(); } },
      ]));
    }

    if (showSec && !fixedS) {
      controlsEl.appendChild(makeRow('Segundo', currentS, [
        { label: '−5', delta: () => { currentS = (currentS + 55) % 60; renderAll(); } },
        { label: '−1', delta: () => { currentS = (currentS + 59) % 60; renderAll(); } },
        { label: '+1', delta: () => { currentS = (currentS + 1) % 60; renderAll(); } },
        { label: '+5', delta: () => { currentS = (currentS + 5) % 60; renderAll(); } },
      ]));
    }
  }

  function makeRow(label: string, value: number | string, buttons: { label: string; delta: () => void }[]): HTMLElement {
    const row = document.createElement('div');
    row.className = 'clock__row';
    const lbl = document.createElement('div');
    lbl.className = 'clock__label';
    lbl.textContent = label;
    row.appendChild(lbl);
    const minusBtns = buttons.filter((b) => b.label.startsWith('−'));
    const plusBtns = buttons.filter((b) => b.label.startsWith('+'));
    for (const b of minusBtns) row.appendChild(makeBtn(b));
    const val = document.createElement('div');
    val.className = 'clock__value';
    val.textContent = String(value).padStart(2, '0');
    row.appendChild(val);
    for (const b of plusBtns) row.appendChild(makeBtn(b));
    return row;
  }

  function makeBtn(b: { label: string; delta: () => void }): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'clock__btn';
    btn.textContent = b.label;
    btn.disabled = buttonsBlocked;
    btn.addEventListener('click', () => {
      if (buttonsBlocked) return;
      ctx.audio.play('click');
      b.delta();
    });
    return btn;
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    if (buttonsBlocked) return;
    buttonsBlocked = true;
    if (currentH === targetH && currentM === targetM && currentS === targetS) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      window.setTimeout(() => {
        advanceSubLevel();
      }, 700);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      window.setTimeout(() => {
        buttonsBlocked = false;
        renderControls();
      }, 700);
    }
  }

  function advanceSubLevel(): void {
    subWithinData++;
    if (subWithinData >= currentConfig().numberOfSubLevels) {
      subWithinData = 0;
      dataIdx++;
    }
    if (dataIdx >= level.data.length) {
      finishLevel();
    } else {
      startQuestion();
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalSubs} horários.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/clockgame/${level.level + 1}`;
      });
    }
  }

  startQuestion();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
